import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "../../Button";
import { Input, Select } from "../../Input";
import toastMessage from "../../../utils/toastMessage";
import { getStorage } from "../../../utils/storage";
import fetchBloodDonation from "../../../utils/queries/bloodDonationQuery";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import centerSiteQuery from "../../../utils/queries/bloodCenterSiteQuery";
import deferralReasons from "../../../constants/deferralReasons";
import api from "../../../utils/api";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

const BLOOD_GROUPS = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];

const DONOR_CATEGORIES = [
  { key: "new", label: "ND (New Donor)" },
  { key: "irregular", label: "ID (Irregular Donor)" },
  { key: "regular", label: "RD (Regular Donor)" },
];

class BloodCollectionReport extends React.Component {
  state = {
    date: moment().format("YYYY-MM-DD"),
    center: null,
    centerSite: null,
    expectedUnits: "",
    centers: [],
    centerSites: [],
    isLoading: false,
    report: null,
  };

  componentDidMount() {
    this.getCenters();
  }

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, {
      type: "dropdown",
    });
    this.setState({ centers: data || [] });
  };

  getCenterSites = async (centerId) => {
    const data = await centerSiteQuery(this.props.defaultLanguage, {
      type: "dropdown",
      center: centerId,
    });
    this.setState({ centerSites: data || [] });
  };

  onChangeCenter = (center) => {
    this.setState({ center, centerSite: null, centerSites: [] });
    if (center?.value) this.getCenterSites(center.value);
  };

  generateReport = async () => {
    const { date, centerSite, expectedUnits } = this.state;
    if (!date || !centerSite) {
      return toastMessage("error", "Please select a date and center site");
    }

    this.setState({ isLoading: true });

    const start = moment(date).startOf("day").format("YYYY-MM-DD HH:mm");
    const end = moment(date).endOf("day").format("YYYY-MM-DD HH:mm");

    try {
      // 1. Fetch all blood collections for this site/date
      const collectionsRes = await fetchBloodDonation(
        this.props.defaultLanguage,
        {
          centerSite: centerSite.value,
          startDate: start,
          endDate: end,
          limit: 1000,
          page: 1,
        }
      );
      const collections = collectionsRes?.data || [];

      // 2. Fetch all vital signs for this site/date (eligible + ineligible)
      const user = await getStorage();
      const vsRes = await api({
        method: "GET",
        url: `${API_URL}/vitalSign`,
        params: {
          centerSite: centerSite.value,
          startDate: start,
          endDate: end,
          limit: 1000,
          page: 1,
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const vitalSigns = vsRes?.data?.data || vsRes?.data || [];

      // 3. Aggregate blood collections
      const totalUnits = collections.length;
      const byCategory = {};
      DONOR_CATEGORIES.forEach((c) => (byCategory[c.key] = 0));
      const byBloodGroup = {};
      BLOOD_GROUPS.forEach((bg) => (byBloodGroup[bg] = 0));

      let adverseReactions = 0;

      collections.forEach((col) => {
        const cat = col.donor?.category;
        if (cat && byCategory[cat] !== undefined) byCategory[cat]++;

        const bg = col.bloodGroup || col.donor?.bloodGroup;
        if (bg && byBloodGroup[bg] !== undefined) byBloodGroup[bg]++;

        if (col.reaction?.isReaction) adverseReactions++;
      });

      // 4. Aggregate vital signs — deferrals
      const eligible = vitalSigns.filter((v) => v.isEligible === true || v.eligible?.isEligible === true);
      const deferred = vitalSigns.filter((v) => v.isEligible === false || v.eligible?.isEligible === false);

      const totalCandidates = vitalSigns.length;
      const totalDeferred = deferred.length;

      const byDeferralReason = {};
      deferralReasons.forEach((r) => (byDeferralReason[r.value] = 0));

      deferred.forEach((v) => {
        const reason = v.eligible?.reason || v.reason;
        if (reason && byDeferralReason[reason] !== undefined) {
          byDeferralReason[reason]++;
        } else if (reason) {
          byDeferralReason["others"] = (byDeferralReason["others"] || 0) + 1;
        }
      });

      this.setState({
        isLoading: false,
        report: {
          date,
          site: centerSite.label,
          expectedUnits: parseInt(expectedUnits) || 0,
          totalCandidates,
          totalUnits,
          byCategory,
          byBloodGroup,
          totalDeferred,
          byDeferralReason,
          adverseReactions,
        },
      });
    } catch (err) {
      toastMessage("error", err);
      this.setState({ isLoading: false });
    }
  };

  printReport = () => {
    window.print();
  };

  renderReport() {
    const { report } = this.state;
    if (!report) return null;

    const {
      date,
      site,
      expectedUnits,
      totalCandidates,
      totalUnits,
      byCategory,
      byBloodGroup,
      totalDeferred,
      byDeferralReason,
      adverseReactions,
    } = report;

    return (
      <div className="blood-collection-report" id="printable-report">
        <style>{`
          @media print {
            body > *:not(#printable-report) { display: none; }
            #printable-report { display: block !important; }
            .no-print { display: none !important; }
          }
          .blood-collection-report { font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 24px; }
          .report-title { text-align: center; font-weight: bold; font-size: 18px; margin-bottom: 4px; }
          .report-subtitle { text-align: center; font-size: 13px; margin-bottom: 16px; color: #555; }
          .report-meta { margin-bottom: 16px; font-size: 14px; }
          .report-meta span { font-weight: bold; }
          .report-section { margin-bottom: 14px; }
          .report-section-title { font-weight: bold; font-size: 14px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 8px; }
          .report-row { display: flex; justify-content: space-between; font-size: 13px; padding: 2px 12px; }
          .report-row.total { font-weight: bold; background: #f5f5f5; }
          .report-row.sub { padding-left: 24px; color: #333; }
          .bg-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; font-size: 13px; }
          .bg-cell { border: 1px solid #ddd; padding: 6px; text-align: center; border-radius: 4px; }
          .bg-cell .bg-label { font-weight: bold; color: #333; }
          .bg-cell .bg-value { font-size: 18px; font-weight: bold; color: #c0392b; }
        `}</style>

        <div className="report-title">BLOOD COLLECTION REPORT</div>
        <div className="report-subtitle">Rwanda Biomedical Centre — Blood Transfusion Division</div>

        <div className="report-meta">
          <div><span>DATE:</span> {moment(date).format("DD/MM/YYYY")}</div>
          <div><span>SITE:</span> {site}</div>
          <div><span>EXPECTED UNITS:</span> {expectedUnits}</div>
        </div>

        {/* A. Total candidates */}
        <div className="report-section">
          <div className="report-row total">
            <span>A. Total candidates</span>
            <span>{totalCandidates}</span>
          </div>
        </div>

        {/* B. Total units collected */}
        <div className="report-section">
          <div className="report-section-title">B. Total units collected: {totalUnits}</div>
          {DONOR_CATEGORIES.map((cat) => (
            <div key={cat.key} className="report-row sub">
              <span>{cat.label}</span>
              <span>{byCategory[cat.key] || 0}</span>
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <div className="bg-grid">
              {BLOOD_GROUPS.map((bg) => (
                <div key={bg} className="bg-cell">
                  <div className="bg-label">{bg}</div>
                  <div className="bg-value">{byBloodGroup[bg] || 0}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* C. Deferrals */}
        <div className="report-section">
          <div className="report-row total">
            <span>C. Deferral</span>
            <span>{totalDeferred}</span>
          </div>
          {deferralReasons.map((r) => (
            <div key={r.value} className="report-row sub">
              <span>— {r.label}</span>
              <span>{byDeferralReason[r.value] || 0}</span>
            </div>
          ))}
        </div>

        {/* D. Intwari Club 25 */}
        <div className="report-section">
          <div className="report-row total">
            <span>D. Intwari Club 25 pledge</span>
            <span>—</span>
          </div>
        </div>

        {/* E. Adverse reactions */}
        <div className="report-section">
          <div className="report-row total">
            <span>E. Adverse reactions</span>
            <span>{adverseReactions}</span>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { date, center, centerSite, centers, centerSites, expectedUnits, isLoading, report } = this.state;

    return (
      <div>
        {/* Filters */}
        <div className="no-print row" style={{ marginBottom: 16 }}>
          <div className="col-md-2">
            <Input
              label="Date"
              type="date"
              value={date}
              onChange={(e) => this.setState({ date: e.target.value })}
            />
          </div>
          <div className="col-md-3">
            <Select
              label="Center"
              options={centers}
              value={center}
              onChange={this.onChangeCenter}
            />
          </div>
          <div className="col-md-3">
            <Select
              label="Site"
              options={centerSites}
              value={centerSite}
              onChange={(v) => this.setState({ centerSite: v })}
              isDisabled={!center}
            />
          </div>
          <div className="col-md-2">
            <Input
              label="Expected Units"
              type="number"
              value={expectedUnits}
              onChange={(e) => this.setState({ expectedUnits: e.target.value })}
            />
          </div>
          <div className="col-md-2" style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <Button
              text="Generate"
              onPress={this.generateReport}
              isSubmitting={isLoading}
            />
            {report && (
              <Button
                text="Print"
                onPress={this.printReport}
                className="btn-default"
              />
            )}
          </div>
        </div>

        {this.renderReport()}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(BloodCollectionReport);
